import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getHomeData } from '../services/coreService'; // Adjust path
import LoadingIndicator from '../components/LoadingIndicator';

const AuthContext = createContext(null);

const getStoredUser = () => {
    const storedUserData = localStorage.getItem('userData');
    if (!storedUserData) return null;
    try {
        return JSON.parse(storedUserData);
    } catch (e) {
        console.error("AuthContext: Failed to parse stored user data", e);
        localStorage.removeItem('userData');
        return null;
    }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [tokens, setTokens] = useState(() => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      return accessToken ? { access: accessToken, refresh: refreshToken } : null;
  });
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
     localStorage.removeItem('accessToken');
     localStorage.removeItem('refreshToken');
     localStorage.removeItem('userData');
     setTokens(null);
     setUser(null);
     console.log("AuthContext: Logged out.");
  }, []);

  const fetchAndUpdateUserProfile = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) setLoading(true);
    const currentAccessToken = localStorage.getItem('accessToken');
    if (!currentAccessToken) {
        setUser(null);
        if (!isInitialLoad) setLoading(false);
        return null;
    }
    try {
      const homeData = await getHomeData();
      const userData = {
          profile: homeData.profile,
          vip_level_image: homeData.image,
          vip_level_name: homeData.level,
          total_profit: homeData.total_profit,
          daily_profit: homeData.daily_profit,
          total_balance: homeData.total_balance,
      };
      setUser(userData);
      localStorage.setItem('userData', JSON.stringify(userData));
      if (!isInitialLoad) setLoading(false);
      return userData;
    } catch (error) {
      console.error("AuthContext: Failed to fetch user data.", error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
         logout();
      }
      if (!isInitialLoad) setLoading(false);
      return null;
    }
  }, [logout]);

  const login = useCallback(async (newTokens) => {
    localStorage.setItem('accessToken', newTokens.access);
    localStorage.setItem('refreshToken', newTokens.refresh);
    setTokens(newTokens);
    await fetchAndUpdateUserProfile();
  }, [fetchAndUpdateUserProfile]);

  useEffect(() => {
     const accessToken = localStorage.getItem('accessToken');
     if (accessToken && !user) {
         fetchAndUpdateUserProfile(true).finally(() => setLoading(false));
     } else {
         setLoading(false);
     }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoggedIn = !!tokens && !!user;
  const value = { tokens, user, isLoggedIn, login, logout, loading, refreshUserProfile: fetchAndUpdateUserProfile };

  return (
    <AuthContext.Provider value={value}>
      {/* Use LoadingIndicator component */}
      {loading ? <LoadingIndicator text="Loading Application..." /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};